import { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EnrichedParentStopPlace,
  StopPlaceInfoSpots,
} from '../../../../../../../types';
import { showSuccessToast } from '../../../../../../../utils';
import { submitFormByRef } from '../../../../../../common/Forms';
import { useInfoContainerControls } from '../../../../../../common/InfoContainer';
import { TerminalInfoSpotsForm } from '../Edit/TerminalInfoSpotsForm';
import { useEditTerminalInfoSpots } from '../Queries';
import { TerminalInfoSpotsViewCard } from '../TerminalInfoSpotsViewCard';
import { TerminalInfoSpotFormState, TerminalInfoSpotRowProps } from '../Types';
import { mapTerminalInfoSpotDataToFormState } from '../utils';
import { TerminalInfoSpotRowHeader } from './TerminalInfoSpotRowHeader';

function useExistingInfoSpotFormDefaultValues(
  infoSpot: Readonly<StopPlaceInfoSpots>,
  terminal: Readonly<EnrichedParentStopPlace>,
) {
  const terminalInfoSpotsFormDefaultValues = useMemo(
    () => mapTerminalInfoSpotDataToFormState(infoSpot, terminal),
    [infoSpot, terminal],
  );

  return { terminalInfoSpotsFormDefaultValues };
}

export const TerminalInfoSpotRow: FC<TerminalInfoSpotRowProps> = ({
  infoSpot,
  index,
  terminal,
  openByDefault,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(openByDefault ?? false);

  const { saveTerminalInfoSpots, defaultErrorHandler } =
    useEditTerminalInfoSpots();

  const ariaControls = `TerminalInfoSpotsViewList::viewCard::${infoSpot.id ?? ''}`;

  const formRef = useRef<HTMLFormElement>(null);
  const infoContainerControls = useInfoContainerControls({
    isExpandable: true,
    isEditable: true,
    onSave: () => submitFormByRef(formRef),
  });
  const { isInEditMode } = infoContainerControls;

  const { terminalInfoSpotsFormDefaultValues } =
    useExistingInfoSpotFormDefaultValues(infoSpot, terminal);

  const onSubmit = async (state: TerminalInfoSpotFormState) => {
    try {
      await saveTerminalInfoSpots({ state });

      showSuccessToast(t(($) => $.terminalDetails.editSuccess));
      infoContainerControls.setIsInEditMode(false);
      setIsOpen(true);
    } catch (err) {
      defaultErrorHandler(err as Error);
    }
  };

  return (
    <>
      <TerminalInfoSpotRowHeader
        infoSpot={infoSpot}
        index={index}
        terminal={terminal}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        controls={infoContainerControls}
        ariaControls={ariaControls}
      />

      {isOpen && !isInEditMode && (
        <tr id={ariaControls}>
          <td colSpan={7} className="p-0">
            <div className="border-b border-border-hsl-commuter-train-purple shadow-lg">
              <TerminalInfoSpotsViewCard
                infoSpot={infoSpot}
                terminal={terminal}
              />
            </div>
          </td>
        </tr>
      )}

      {isOpen && isInEditMode && (
        <tr>
          <td colSpan={7} className="p-0">
            <div className="border-border-hsl-commuter-train-purple shadow-lg">
              <TerminalInfoSpotsForm
                defaultValues={terminalInfoSpotsFormDefaultValues}
                infoSpot={infoSpot}
                ref={formRef}
                terminal={terminal}
                onSubmit={onSubmit}
                onCancel={() => infoContainerControls.onCancel()}
                testIdPrefix="TerminalInfoSpotRow"
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
